import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, Heart, Layers, PackagePlus, Bell, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { trackHome } from '@/lib/homeAnalytics';

// Rows mirror the fields shown on the real comparison page.
const COMPARE_ROWS = ['Description', 'Starting price', 'Free tier', 'Pricing details', 'Pros', 'Cons'];

const STEPS = [
  { n: '1', title: 'Pick tools', text: 'Tap the compare icon on any tool card in the directory.' },
  { n: '2', title: 'Add up to four', text: 'Your picks collect in a bar at the bottom of the screen.' },
  { n: '3', title: 'See them side by side', text: 'Open the comparison to check pricing, free tiers, pros and cons.' },
];

export function CompareSection() {
  return (
    <section className="container mx-auto px-4 py-16 md:py-20">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-2">Compare</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">Find the better fit.</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Two tools can look alike until you line up their pricing, free tiers and trade-offs. Comparing first saves
            you from signing up for the wrong one.
          </p>
          <ol className="space-y-4 mb-8">
            {STEPS.map((s) => (
              <li key={s.n} className="flex gap-4">
                <span className="h-8 w-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center shrink-0">{s.n}</span>
                <div>
                  <p className="font-medium">{s.title}</p>
                  <p className="text-sm text-muted-foreground">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <Button size="lg" asChild>
              <Link to="/tools" onClick={() => trackHome('compare_cta_clicked', 'choose_tools')}>
                Choose tools to compare <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
            <Link
              to="/compare/vibe-coding"
              onClick={() => trackHome('compare_cta_clicked', 'example_vibe_coding')}
              className="text-sm font-medium text-primary hover:underline"
            >
              See an example: vibe-coding tools
            </Link>
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card overflow-hidden" aria-hidden>
          <div className="grid grid-cols-3 text-xs font-medium text-muted-foreground border-b border-border/60">
            <div className="p-3">What you compare</div>
            <div className="p-3 border-l border-border/60">Your pick</div>
            <div className="p-3 border-l border-border/60">Your pick</div>
          </div>
          {COMPARE_ROWS.map((r) => (
            <div key={r} className="grid grid-cols-3 text-sm border-b last:border-b-0 border-border/60">
              <div className="p-3 font-medium">{r}</div>
              <div className="p-3 border-l border-border/60"><span className="block h-2 mt-1.5 w-3/4 rounded bg-muted" /></div>
              <div className="p-3 border-l border-border/60"><span className="block h-2 mt-1.5 w-2/3 rounded bg-muted" /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Only benefits that exist today: favorites, My Stack, recently viewed, submissions, notifications.
const BENEFITS = [
  { icon: Heart, text: 'Save favorite tools' },
  { icon: Layers, text: 'Keep a personal shortlist in My Stack' },
  { icon: History, text: 'Pick up where you left off with recently viewed tools' },
  { icon: PackagePlus, text: 'Submit and manage your own AI tools' },
  { icon: Bell, text: 'Get notified about updates you care about' },
];

export function AccountSection() {
  const { user } = useAuth();
  const location = useLocation();
  // Return visitors to where they were (same-site path only; Auth validates it again).
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <section className="border-y border-border/60 bg-muted/20">
      <div className="container mx-auto px-4 py-20 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Found something useful? Keep it.
          </h2>
          <p className="text-muted-foreground text-lg mb-10">
            Browsing and searching work without an account. A free account lets you save favorites and build your own shortlist in My Stack.
          </p>
          <ul className="text-left max-w-md mx-auto space-y-3 mb-10">
            {BENEFITS.map((b) => (
              <li key={b.text} className="flex items-center gap-3">
                <Check className="h-4 w-4 text-primary shrink-0" aria-hidden />
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          {user ? (
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" variant="outline" asChild><Link to="/favorites">Your favorites</Link></Button>
              <Button size="lg" variant="outline" asChild><Link to="/my-stack">Your stack</Link></Button>
            </div>
          ) : (
            <Button size="lg" asChild>
              <Link to={`/auth?mode=signup&next=${next}`} onClick={() => trackHome('register_cta_clicked')}>
                Create Your Free Account
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

export function CreatorStrip() {
  return (
    <section className="container mx-auto px-4 pt-10 pb-6">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-border/60 px-6 py-5">
        <p className="font-medium">Have an AI tool? <span className="text-muted-foreground font-normal">Submit it to be listed in the directory.</span></p>
        <Button variant="outline" asChild>
          <Link to="/submit">Submit your tool</Link>
        </Button>
      </div>
    </section>
  );
}
